import React, { useState, useEffect, useContext, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Bell, X, AlertTriangle } from 'lucide-react';
import { requestNotificationPermission, subscribeToPush } from '../../utils/serviceWorker';
import { AuthContext } from '../../context/AuthContext';
import { contesto } from '../../i18n';

// Invito ad attivare le notifiche del browser (e istruzioni se sono bloccate). Solo con un utente entrato: segue
// il login e l'uscita (niente banner sulla pagina di accesso dopo "Esci" o con la sessione scaduta, e compare dopo
// un login senza ricaricare). Testi al "voi", istruzioni valide per ogni browser.
// Il banner sta fuori dal router (App.js), quindi la lingua la prende dall'utente collegato (AuthContext.lingua):
// quella del cliente, italiano per lo staff.
//
// Il CLIENTE non lo vede più: nella sua home c'è la card «Attiva le notifiche» (components/cliente/CardNotifiche),
// che spiega a cosa servono e cosa fare sull'iPhone. Il banner resta per admin e collaboratori, che quella home
// non ce l'hanno (nel loro profilo c'è la sezione Notifiche).

const suIPhone = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

// Il permesso c'è già: si rinfresca l'iscrizione di questo dispositivo, senza toccare quella buona (prima la si
// cancellava e se ne creava una nuova a ogni caricamento: sul server nasceva un dispositivo nuovo ogni volta)
const resubscribe = async () => {
  try {
    await subscribeToPush();
  } catch (e) {
    console.log('Resubscribe error:', e);
  }
};

// Il banner è fisso in basso e copriva i bottoni in fondo alla pagina (es. "Annulla" del modulo dei familiari): finché
// c'è, sulla pagina html ci sono la classe mk-con-banner e la sua altezza in --mk-banner, e le pagine del cliente
// lasciano quello spazio sotto il contenuto (cliente.css)
const useSpazioBanner = (ref, visibile) => {
  useEffect(() => {
    const radice = document.documentElement;
    const el = ref.current;
    if (!visibile || !el) return undefined;
    const misura = () => radice.style.setProperty('--mk-banner', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    misura();
    radice.classList.add('mk-con-banner');
    const osserva = typeof ResizeObserver === 'function' ? new ResizeObserver(misura) : null;
    osserva?.observe(el);
    window.addEventListener('resize', misura);
    return () => {
      osserva?.disconnect();
      window.removeEventListener('resize', misura);
      radice.classList.remove('mk-con-banner');
      radice.style.removeProperty('--mk-banner');
    };
  }, [ref, visibile]);
};

const NotificationBanner = () => {
  const auth = useContext(AuthContext);
  const { t } = contesto(auth?.lingua);
  // il cliente ha la card nella sua home: qui resta lo staff
  const entrato = Boolean(auth?.user) && auth?.userType !== 'cliente';
  const idUtente = auth?.user?.id || auth?.user?.email || null;
  const [show, setShow] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const banner = useRef(null);
  const visibile = entrato && (show || showBlocked);
  useSpazioBanner(banner, visibile ? (showBlocked ? 'bloccate' : 'invito') : null);

  useEffect(() => {
    setShow(false);
    setShowBlocked(false);
    if (!entrato || !('Notification' in window)) return undefined;

    const permission = Notification.permission;
    let timer;
    if (permission === 'default') {
      // Mai chiesto — mostra banner
      if (!localStorage.getItem('mk_notif_dismissed')) timer = setTimeout(() => setShow(true), 3000);
    } else if (permission === 'granted') {
      // Già concesso — ri-subscribe silenziosamente
      resubscribe();
    } else if (permission === 'denied') {
      // Bloccato — mostra istruzioni
      if (!localStorage.getItem('mk_notif_blocked_dismissed')) timer = setTimeout(() => setShowBlocked(true), 5000);
    }
    return () => clearTimeout(timer);
  }, [entrato, idUtente]);

  const handleAllow = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      const sub = await subscribeToPush();
      setShow(false);
      localStorage.setItem('mk_notif_granted', 'true');
      // Senza iscrizione (iPhone fuori dalla app installata) il permesso resta: non si mostra la conferma
      if (sub && window.Notification && Notification.permission === 'granted') {
        new Notification('MK Client', { body: t('comune.notifiche_attivate'), icon: '/apple-touch-icon.png' });
      }
    } else {
      setShow(false);
      setShowBlocked(true);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('mk_notif_dismissed', 'true');
  };

  const handleDismissBlocked = () => {
    setShowBlocked(false);
    localStorage.setItem('mk_notif_blocked_dismissed', 'true');
  };

  if (!entrato) return null;

  // Banner notifiche bloccate
  if (showBlocked) {
    return (
      <div ref={banner} className="mk-sopra-barra fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4" data-testid="notification-blocked-banner">
        <div className="bg-card border border-orange-500/30 rounded-xl p-4 shadow-2xl backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{t('comune.notifiche_bloccate_titolo')}</p>
              <p className="text-xs text-white/60 mt-1">{t('comune.notifiche_bloccate_testo')}</p>
              {suIPhone() ? (
                <ol className="text-xs text-white/50 mt-2 space-y-1 list-decimal list-inside">
                  <li>{t('comune.notifiche_iphone_1')}</li>
                  <li>{t('comune.notifiche_iphone_2')}</li>
                </ol>
              ) : (
                <ol className="text-xs text-white/50 mt-2 space-y-1 list-decimal list-inside">
                  <li>{t('comune.notifiche_browser_1')}</li>
                  <li>{t('comune.notifiche_browser_2')}</li>
                  <li>{t('comune.ricarica_pagina')}</li>
                </ol>
              )}
              <Button size="sm" variant="ghost" className="text-white/40 hover:text-white text-xs h-6 mt-2 p-0" onClick={handleDismissBlocked}>
                {t('comune.ho_capito')}
              </Button>
            </div>
            <button type="button" onClick={handleDismissBlocked} className="text-white/30 hover:text-white/60" aria-label={t('comune.chiudi')}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!show) return null;

  return (
    <div ref={banner} className="mk-sopra-barra fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4" data-testid="notification-banner">
      <div className="bg-card border border-gold/30 rounded-xl p-4 shadow-2xl backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
            <Bell className="h-5 w-5 text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">{t('comune.notifiche_invito_titolo')}</p>
            <p className="text-xs text-white/60 mt-0.5">
              {t('comune.notifiche_invito_testo')}
              {suIPhone() && t('comune.notifiche_iphone')}
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="bg-gold text-black hover:bg-gold/90 text-xs h-7" onClick={handleAllow}>
                {t('comune.notifiche_attiva')}
              </Button>
              <Button size="sm" variant="ghost" className="text-white/50 hover:text-white text-xs h-7" onClick={handleDismiss}>
                {t('comune.notifiche_non_ora')}
              </Button>
            </div>
          </div>
          <button type="button" onClick={handleDismiss} className="text-white/30 hover:text-white/60" aria-label={t('comune.chiudi')}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export { NotificationBanner };
